import React from "react";
import { observer } from "mobx-react";
import { Stack, Text, HStack, Box, Image, Button } from "@chakra-ui/react";
import { t } from "i18next";
import { DistanceUnit, getOptionTranslation } from "@keita/lanepatrol-sdk";

import { useStores } from "../../shared/stores/RootStore";
import { HlaScoreView } from "./HlaScoreView";
import { HlaLane } from "../models/HlaLane";
import { SimulationLane } from "../models/SimulationLane";
import { HlaWay } from "../models/HlaWay";
import { SimulationWay } from "../models/SimulationWay";
import { hlaAttributeOptions } from "../../map/models/HlaAttribute";
import { CrossSectionView } from "./HlaWayDetail/cross-section/CrossSectionView";
import { StreetViewComponent } from "./StreetView/StreetViewComponent";
import {
  ATTRIBUTE_LABELS,
  AttributeChange,
  HlaSimulationProps,
  Lane,
  LANE_FIELDS,
  WAY_FIELDS,
  WayLike,
} from "../models/ComparisonFields";

const isEqual = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

const humanizeKey = (key: string): string => {
  const withSpaces = key.replace(/([A-Z])/g, " $1").toLowerCase();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

const attributeLabel = (key: string): string =>
  ATTRIBUTE_LABELS[key] ??
  t(`shared:hlaAttribute.${key}`, {
    defaultValue: humanizeKey(key),
  });

const getSpeedUnit = (distanceUnit: DistanceUnit): string =>
  distanceUnit === DistanceUnit.Imperial ? "mph" : "km/h";

const getOptionLabel = (value: unknown): string | null => {
  const optionGroups = Object.values(hlaAttributeOptions).filter(Boolean);

  for (const options of optionGroups) {
    const option = options.find((item) => item.value === value);
    if (option) return t(option.translation);
  }

  if (typeof value === "string") {
    const translation = getOptionTranslation(value as any);
    if (translation) return t(translation);
  }

  return null;
};

const formatScalar = (value: unknown): string => {
  if (typeof value === "boolean") return value ? "Yes" : "No";

  return getOptionLabel(value) ?? String(value);
};

const formatNumber = (value: number): string => {
  return value.toLocaleString();
};

const getChangedObjectKeys = (value: unknown, other: unknown): string[] => {
  const valueObj = (value ?? {}) as Record<string, unknown>;
  const otherObj = (other ?? {}) as Record<string, unknown>;

  return Array.from(
    new Set([...Object.keys(valueObj), ...Object.keys(otherObj)]),
  ).filter((key) => !isEqual(valueObj[key], otherObj[key]));
};

const formatValue = (value: unknown, other: unknown): string => {
  if (value === null || value === undefined) return "No attribute selected";

  if (typeof value === "object" && !Array.isArray(value)) {
    const valueObj = value as Record<string, unknown>;
    const changedKeys = getChangedObjectKeys(value, other);

    if (changedKeys.length === 0) return "No change";

    return changedKeys
      .map((key) => `${attributeLabel(key)}: ${formatScalar(valueObj[key])}`)
      .join("\n");
  }

  return formatScalar(value);
};

const formatChangeValue = (
  changeKey: string,
  value: unknown,
  other: unknown,
  speedUnit: string,
): string => {
  const formattedValue = formatValue(value, other);

  if (
    changeKey === "averageRoadSpeed" &&
    value !== null &&
    value !== undefined
  ) {
    return `${formattedValue} ${speedUnit}`;
  }

  return formattedValue;
};

function diffFields<
  Before extends object,
  After extends object,
  Key extends keyof Before & keyof After,
>(
  before: Before | null | undefined,
  after: After | null | undefined,
  fields: { key: Key; label: string }[],
): AttributeChange[] {
  if (!before || !after) return [];

  return fields
    .filter(({ key }) => !isEqual(before[key], after[key]))
    .map(({ key, label }) => ({
      key: String(key),
      label,
      hlaValue: before[key],
      simulationValue: after[key],
    }));
}

const getWayChanges = (
  hlaWay?: HlaWay,
  simulationWay?: SimulationWay,
): AttributeChange[] => diffFields(hlaWay, simulationWay, WAY_FIELDS);

const getLaneChanges = (
  hlaLane?: HlaLane,
  simulationLane?: SimulationLane,
): AttributeChange[] => diffFields(hlaLane, simulationLane, LANE_FIELDS);

const PhotoBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    w="100%"
    h="400px"
    aspectRatio={1}
    borderWidth="1px"
    borderRadius="md"
    overflow="hidden"
  >
    {children}
  </Box>
);

const ComparisonPhoto = ({
  photoUrl,
  alt,
  streetViewMapSource,
}: {
  photoUrl?: string | null;
  alt: string;
  streetViewMapSource?: React.ComponentProps<
    typeof StreetViewComponent
  >["streetViewMapSource"];
}) => (
  <PhotoBox>
    {photoUrl ? (
      <Image src={photoUrl} alt={alt} h="100%" w="100%" objectFit="cover" />
    ) : (
      <StreetViewComponent streetViewMapSource={streetViewMapSource} />
    )}
  </PhotoBox>
);

const ComparisonCard = ({
  title,
  photo,
  children,
}: {
  title: string;
  photo: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    flex={1}
    borderWidth="1px"
    borderRadius="xl"
    bg="white"
    p={5}
    boxShadow="sm"
  >
    <Stack gap={5}>
      <Text fontSize="2xl" fontWeight="bold" textAlign="center">
        {title}
      </Text>

      <HStack gap={6} align="start">
        <Box flex={"0 0 65%"}>{photo}</Box>
        <Box flex={2} minW={0}>
          {children}
        </Box>
      </HStack>
    </Stack>
  </Box>
);

const WayStats = ({
  averageRoadSpeed,
  averageDailyTraffic,
  type,
  speedUnit,
}: Pick<WayLike, "averageRoadSpeed" | "averageDailyTraffic" | "type"> & {
  speedUnit: string;
}) => {
  const { surveyStore } = useStores();

  const roadSpeed =
    averageRoadSpeed ??
    (type ? surveyStore.getDefaultAverageRoadSpeed(type) : null);

  const aadt =
    averageDailyTraffic ??
    (type ? surveyStore.getDefaultAverageDailyTraffic(type) : null);

  return (
    <Stack
      gap={1}
      p={3}
      borderWidth="1px"
      borderRadius="md"
      minW="120px"
      fontSize="sm"
    >
      <Text fontSize="xs" color="gray.500">
        Road speed
      </Text>
      <Text fontWeight="semibold">{`${roadSpeed} ${speedUnit}`}</Text>

      <Text fontSize="xs" color="gray.500" mt={2}>
        AADT
      </Text>
      <Text fontWeight="semibold">{formatNumber(aadt!!)}</Text>
    </Stack>
  );
};

type LaneComparisonColumnProps = {
  way?: WayLike;
  lane?: Lane;
  score?: React.ComponentProps<typeof HlaScoreView>["score"];
  speedUnit: string;
  editLabel: string;
  onEdit: () => void;
};

const LaneComparisonColumn = ({
  way,
  lane,
  score,
  speedUnit,
  editLabel,
  onEdit,
}: LaneComparisonColumnProps) => (
  <Stack h="100%" w="100%" minH="300px" gap={4}>
    {lane && way && (
      <Stack align="center" gap={4} minW={0}>
        <Box minW={0} overflow="hidden" w="100%">
          <CrossSectionView
            lit={!!way.lit}
            lane={lane}
            canEdit={false}
            isSelected={false}
            onSelect={() => {}}
            onDelete={() => {}}
          />
        </Box>

        <WayStats
          averageRoadSpeed={way.averageRoadSpeed}
          averageDailyTraffic={way.averageDailyTraffic}
          type={way.type}
          speedUnit={speedUnit}
        />
      </Stack>
    )}

    <Box>
      <HlaScoreView score={score} />
    </Box>

    <Button
      variant="outline"
      colorPalette="primary"
      size="sm"
      fontSize="sm"
      alignSelf="flex-end"
      mt="auto"
      onClick={onEdit}
    >
      {editLabel}
    </Button>
  </Stack>
);

const ChangeCard = ({
  change,
  speedUnit,
}: {
  change: AttributeChange;
  speedUnit: string;
}) => (
  <Box borderWidth="1px" borderRadius="md" p={3}>
    <Text fontWeight="semibold" fontSize="sm" mb={2} textAlign="center">
      {change.label}
    </Text>

    <HStack justify="space-between" align="start">
      <Box flex={1} textAlign="center">
        <Text fontSize="xs" color="gray.500">
          Before
        </Text>
        <Text fontSize="sm" whiteSpace="pre-line">
          {formatChangeValue(
            change.key,
            change.hlaValue,
            change.simulationValue,
            speedUnit,
          )}
        </Text>
      </Box>

      <Box flex={1} textAlign="center">
        <Text fontSize="xs" color="gray.500">
          After
        </Text>
        <Text fontSize="sm" whiteSpace="pre-line">
          {formatChangeValue(
            change.key,
            change.simulationValue,
            change.hlaValue,
            speedUnit,
          )}
        </Text>
      </Box>
    </HStack>
  </Box>
);

const WhatChanged = ({
  changes,
  speedUnit,
}: {
  changes: AttributeChange[];
  speedUnit: string;
}) => (
  <Box
    borderWidth="1px"
    borderRadius="xl"
    bg="white"
    p={5}
    boxShadow="sm"
    flex="1"
    minH={0}
    display="flex"
    flexDirection="column"
  >
    <Stack gap={3} flex="1" minH={0}>
      <Text fontWeight="bold" flexShrink={0}>
        What changed
      </Text>

      {changes.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No attribute changes detected.
        </Text>
      ) : (
        <Stack gap={2} flex="1" minH={0} overflowY="auto" pr={1}>
          {changes.map((change) => (
            <ChangeCard
              key={change.key}
              change={change}
              speedUnit={speedUnit}
            />
          ))}
        </Stack>
      )}
    </Stack>
  </Box>
);

const HlaSimulationComparisonViewBase: React.FC<HlaSimulationProps> = ({
  distanceUnit,
}) => {
  const { hlaStore, simulationStore, mapStore } = useStores();

  const speedUnit = getSpeedUnit(distanceUnit);

  const hlaWay = hlaStore.getSelected();
  const hlaLane = hlaStore.getSelectedLane();

  const simulationWay = simulationStore.selectedSimulationWay;
  const simulationLane = simulationStore.getSelectedLane();

  const changes = [
    ...getWayChanges(hlaWay, simulationWay),
    ...getLaneChanges(hlaLane, simulationLane),
  ];

  const afterPhotoUrl = simulationWay?.photo?.url;

  return (
    <Stack gap={6} py={4} px={6} h="100%" minH={0} overflow="hidden" w="full">
      <Text fontSize="xl" fontWeight="bold" flexShrink={0}>
        Comparison view
      </Text>

      <HStack gap={6} flexShrink={0}>
        <ComparisonCard
          title="Before"
          photo={
            <ComparisonPhoto
              photoUrl={null}
              alt="HLA reference"
              streetViewMapSource={mapStore.streetViewMapSource}
            />
          }
        >
          <LaneComparisonColumn
            way={hlaWay}
            lane={hlaLane}
            score={hlaLane?.score!!}
            speedUnit={speedUnit}
            editLabel="Edit HLA"
            onEdit={() => hlaStore.editHla()}
          />
        </ComparisonCard>

        <ComparisonCard
          title="After"
          photo={
            <ComparisonPhoto
              photoUrl={afterPhotoUrl}
              alt="Simulation reference"
              streetViewMapSource={mapStore.streetViewMapSource}
            />
          }
        >
          <LaneComparisonColumn
            way={simulationWay}
            lane={simulationLane}
            score={simulationLane?.score!!}
            speedUnit={speedUnit}
            editLabel="Edit simulation"
            onEdit={() => simulationStore.editSimulation()}
          />
        </ComparisonCard>
      </HStack>

      <WhatChanged changes={changes} speedUnit={speedUnit} />
    </Stack>
  );
};

export const HlaSimulationComparisonView = observer(
  HlaSimulationComparisonViewBase,
);
